'use client';
import React, { useEffect, useRef } from 'react';

const travelNeeds = [
  {
    number: '01',
    title: 'Autonomie',
    description: 'Les voyageurs veulent accéder aux informations par eux-mêmes, à leur rythme, sans attendre une réponse.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><path d="M12 8v4l3 3" />
      </svg>
    ),
  },
  {
    number: '02',
    title: 'Clarté',
    description: 'Des instructions limpides pour l\'arrivée, le départ, les équipements — sans ambiguïté ni frustration.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
    ),
  },
  {
    number: '03',
    title: 'Rapidité',
    description: 'L\'information disponible instantanément, sur mobile, à n\'importe quelle heure du jour ou de la nuit.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
      </svg>
    ),
  },
  {
    number: '04',
    title: 'Fluidité',
    description: 'Un parcours d\'accueil sans friction, de la confirmation de réservation jusqu\'au départ.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
      </svg>
    ),
  },
  {
    number: '05',
    title: 'Recommandations',
    description: 'Des adresses locales, des conseils d\'initié — ce qui transforme un séjour ordinaire en souvenir inoubliable.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" />
      </svg>
    ),
  },
];

export default function WhySection() {
  const sectionRef = useRef<HTMLElement>(null);
  const scrollRevealRef = useRef<HTMLElement>(null);
  const wordsRef = useRef<NodeListOf<Element> | null>(null);

  useEffect(() => {
    // Animate on scroll observer
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    sectionRef.current?.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));

    // Scroll reveal words
    const words = document.querySelectorAll('.reveal-word');
    wordsRef.current = words;

    const handleScroll = () => {
      const section = scrollRevealRef.current;
      if (!section) return;
      const rect = section.getBoundingClientRect();
      const winH = window.innerHeight;
      const startReveal = winH * 0.9;
      const endReveal = winH * 0.2;
      let progress = (startReveal - rect.top) / (startReveal - endReveal);
      progress = Math.max(0, Math.min(1, progress));
      const activeCount = Math.floor(progress * words.length);
      words.forEach((w, i) => {
        if (i < activeCount) w.classList.add('active');
        else w.classList.remove('active');
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <section id="pourquoi" ref={sectionRef} className="relative z-10 bg-white">
      {/* Marquee strip */}
      <div className="border-y border-primary/10 py-5 overflow-hidden bg-primary/3">
        <div className="scrolling-text-container">
          <div className="scrolling-text text-foreground-subtle text-[10px] uppercase tracking-ultra font-medium">
            {Array(4).fill(null).map((_, i) => (
              <React.Fragment key={i}>
                <span className="mx-10">Accueil digital</span>
                <span className="mx-3 text-accent">·</span>
                <span className="mx-10">Livret numérique</span>
                <span className="mx-3 text-accent">·</span>
                <span className="mx-10">Expérience voyageur</span>
                <span className="mx-3 text-accent">·</span>
                <span className="mx-10">Guadeloupe</span>
                <span className="mx-3 text-accent">·</span>
                <span className="mx-10">Anse des Rochers</span>
                <span className="mx-3 text-accent">·</span>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll reveal headline */}
      <section
        ref={scrollRevealRef}
        className="py-28 md:py-40 border-b border-primary/8"
      >
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="mb-10 animate-on-scroll anim-in" style={{ animationPlayState: 'paused' }}>
            <span className="text-[10px] uppercase tracking-ultra text-accent font-medium">
              Comprendre l'évolution
            </span>
          </div>
          <h2 className="font-serif-display text-4xl md:text-6xl leading-tight text-foreground">
            {[
              'Les', 'voyageurs', "d'aujourd'hui", 'attendent', 'plus', 'qu\'un',
              'simple', 'logement.', 'Ils', 'recherchent', 'une', 'expérience',
              'fluide,', 'autonome', 'et', 'mémorable.'
            ].map((word, i) => (
              <span key={i} className={`reveal-word inline-block mr-3 ${word === 'mémorable.' ? 'font-medium text-primary' : ''}`}>
                {word}
              </span>
            ))}
          </h2>
        </div>
      </section>

      {/* 5 needs grid */}
      <div className="py-24 md:py-32">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="mb-16 animate-on-scroll anim-in" style={{ animationPlayState: 'paused' }}>
            <p className="text-sm text-foreground-muted max-w-xl leading-relaxed">
              L'accueil digital n'est pas une tendance passagère. C'est une évolution progressive, 
              portée par les comportements des voyageurs modernes. Voici ce qu'ils attendent.
            </p>
          </div>

          {/* Asymmetric grid — large first card + 4 smaller */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {/* Large card */}
            <div
              className="md:col-span-5 border border-primary/12 p-10 bg-background card-lift animate-on-scroll anim-in delay-100 group"
              style={{ animationPlayState: 'paused' }}
            >
              <div className="flex items-start justify-between mb-8">
                <span className="text-[9px] uppercase tracking-ultra text-foreground-subtle">{travelNeeds[0].number}</span>
                <span className="text-primary">{travelNeeds[0].icon}</span>
              </div>
              <h3 className="font-serif-display text-3xl text-foreground mb-4">{travelNeeds[0].title}</h3>
              <p className="text-sm text-foreground-muted leading-relaxed">{travelNeeds[0].description}</p>
              <div className="mt-8 w-full h-px bg-gradient-to-r from-primary/30 to-transparent group-hover:from-primary/60 transition-all duration-500" />
            </div>

            {/* Right column: 2x2 smaller cards */}
            <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {travelNeeds.slice(1).map((need, i) => (
                <div
                  key={need.number}
                  className={`border border-primary/10 p-8 bg-white card-lift animate-on-scroll anim-in group`}
                  style={{ animationPlayState: 'paused', animationDelay: `${(i + 2) * 0.1}s` }}
                >
                  <div className="flex items-start justify-between mb-6">
                    <span className="text-[9px] uppercase tracking-ultra text-foreground-subtle">{need.number}</span>
                    <span className="text-accent">{need.icon}</span>
                  </div>
                  <h3 className="font-serif-display text-xl text-foreground mb-3">{need.title}</h3>
                  <p className="text-xs text-foreground-muted leading-relaxed">{need.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Pédagogie note */}
          <div
            className="mt-14 p-8 border border-accent/20 bg-accent/4 animate-on-scroll anim-in"
            style={{ animationPlayState: 'paused' }}
          >
            <div className="flex items-start gap-4">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-accent mt-0.5 shrink-0">
                <circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" />
              </svg>
              <p className="text-sm text-foreground-muted leading-relaxed">
                <strong className="text-foreground font-medium">L'accueil digital devient progressivement un standard.</strong>{' '}
                Pas une obligation immédiate, mais une évolution naturelle qui améliore le quotidien des propriétaires 
                et la satisfaction des voyageurs. Anse des Rochers vous accompagne dans cette transition, à votre rythme.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}